import React, { FunctionComponent, memo, useEffect, useState } from "react";
import cn from "classnames";
import styles from "./PartsModal.module.scss";
import { SectionItemI } from "../../../shared";
import {useShelvingStore, useStoreSystem} from "../../../store";
import shallow from "zustand/shallow";
import { SectionInterface } from "../../../store/types/store";
import { Button } from "@mui/material";
import { isNewSite } from "../../../utils";

interface PartsModalBodyProps {
    editItem: SectionItemI;
    editSection: SectionInterface;
}

const isNew = isNewSite();

const PartsModalBody: FunctionComponent<PartsModalBodyProps> = memo(({ editItem, editSection }) => {
    const [selectedEditItem, setSelectedEditItem] = useState<SectionItemI>(editItem);
    const [selectedParts, setSelectedParts] = useState<Set<string>>(new Set());
    const { updateSection, setColorsItems, colorsItems } = useShelvingStore(
        (state) => ({
            updateSection: state.updateSection,
            setColorsItems: state.setColorsItems,
            colorsItems: state.colorsItems,
        }),
        shallow
    );

    const { parts } = useStoreSystem((state) => ({
        parts: state.parts,
    }))

    useEffect(() => {
        const data = new Set<string>();
        if (editItem.amps && editItem.edges) {
            const items = [...editItem.amps.left.elements, ...editItem.edges, ...(editItem.rods || [])];
            items.forEach((item) => {
                const inBrackets = editItem.brackets.left.elements.some(
                    (el) =>
                        el.bitrixId === item.bitrixId ||
                        el.bitrixId === item.bitrixIdAlt ||
                        (item.bitrixIdOriginal && el.bitrixId === item.bitrixIdOriginal)
                );
                const inParts = editItem.parts.some(
                    (el) =>
                        el.bitrixId === item.bitrixId ||
                        el.bitrixId === item.bitrixIdAlt ||
                        (item.bitrixIdOriginal && el.bitrixId === item.bitrixIdOriginal) ||
                          (item?.type?.includes('Штанга') ? el.bitrixIdOriginal === item.bitrixIdOriginal : false)
                );
                if (inBrackets || inParts) {
                    data.add(item.bitrixId);
                }
            });
        }
        setSelectedParts(data);
    }, [editItem, parts]);

    const addPartsCurrentItem = (partId: string) => {
        const newEditItem = structuredClone(selectedEditItem);
        const newEditSection = structuredClone(editSection);

        const amp = newEditItem?.amps?.left?.elements?.find((item) => item.bitrixId === partId);
        const edge = newEditItem?.edges?.find((item) => item.bitrixId === partId);
        const rod = newEditItem?.rods?.find((item) => item.bitrixId === partId);

        newEditItem.brackets = {
            ...newEditItem.brackets,
            left: {
                ...newEditItem.brackets.left,
                elements: amp ? [...newEditItem.brackets.left.elements, amp] : [...newEditItem.brackets.left.elements],
            },
            right: {
                ...newEditItem.brackets.right,
                elements: amp
                    ? [...newEditItem.brackets.right.elements, amp]
                    : [...newEditItem.brackets.right.elements],
            },
        };
        newEditItem.parts = [
            ...(edge ? [...newEditItem.parts, edge] : [...newEditItem.parts]),
            ...(rod ? [rod] : []),
        ].filter((item, index, arr) => arr.findIndex((i) => i.bitrixId === item.bitrixId) === index);

        const index = newEditSection.items.findIndex((item) => item.id === newEditItem.id);
        if (index !== -1) {
            newEditSection.items[index] = newEditItem;
        }

        updateSection(newEditSection);
        setSelectedEditItem(newEditItem);
        setSelectedParts((prev) => {
            const newSet = new Set(prev);
            newSet.add(partId);
            return newSet;
        });
        setColorsItems({ ...colorsItems });
    };

    const removePartsCurrentItem = (partId: string) => {
        const newEditItem = structuredClone(selectedEditItem);
        const newEditSection = structuredClone(editSection);

        // Найти штангу среди доступных опций для получения всех возможных ID
        const allItems = [
            ...(newEditItem.amps?.left.elements || []),
            ...(newEditItem.edges || []),
            ...(newEditItem.rods || []),
        ];
        const targetItem = allItems.find((item) => item.bitrixId === partId);

        // Создаем массив всех возможных ID для поиска
        const possibleIds = targetItem
            ? [targetItem.bitrixId, targetItem.bitrixIdAlt, targetItem.bitrixIdOriginal].filter(Boolean)
            : [partId];

        newEditItem.brackets.left.elements = newEditItem.brackets.left.elements.filter(
            (item) => !possibleIds.includes(item.bitrixId)
        );
        newEditItem.brackets.right.elements = newEditItem.brackets.right.elements.filter(
            (item) => !possibleIds.includes(item.bitrixId)
        );
        newEditItem.parts = newEditItem.parts.filter((item) => (item?.type?.includes('Штанга')
          ? item?.bitrixIdOriginal !== targetItem?.bitrixIdOriginal : !possibleIds.includes(item.bitrixId)));

        const index = newEditSection.items.findIndex((item) => item.id === newEditItem.id);
        if (index !== -1) {
            newEditSection.items[index] = newEditItem;
        }

        updateSection(newEditSection);
        setSelectedEditItem(newEditItem);
        setSelectedParts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(partId);
            return newSet;
        });
        setColorsItems({ ...colorsItems });
    };

    return (
        <div className={cn(styles.root)}>
            <div className={styles.header}>{`Выберите доступные опции:`}</div>
            <div className={cn(styles.body, styles.categories)}>
                {/*TODO! Беру только левую (одну) сторону, чтобы вывести в список без дублей*/}
                {selectedEditItem.amps &&
                    selectedEditItem.edges &&
                    [
                        ...selectedEditItem.amps.left.elements,
                        ...selectedEditItem.edges,
                        ...(selectedEditItem.rods || []),
                    ].length > 0 &&
                    [
                        ...selectedEditItem.amps.left.elements,
                        ...selectedEditItem.edges,
                        ...(selectedEditItem.rods || []),
                    ].map((item) => {
                        const isCheck = selectedParts.has(item.bitrixId);
                        return (
                            <div key={item.bitrixId} className={cn(styles.container)}>
                                <img className={styles.img} src={item.picture} />
                                <span className={styles.name}>{item.name}</span>
                                <div className={styles.itemBottom}>
                                    <span className={styles.price}>{item.price}&nbsp;₽</span>
                                    <Button
                                        className={cn(styles.btn, isCheck && styles.btn_hold)}
                                        variant="contained"
                                        onClick={() =>
                                            isCheck
                                                ? removePartsCurrentItem(item.bitrixId)
                                                : addPartsCurrentItem(item.bitrixId)
                                        }
                                    >
                                        {isCheck ? "Удалить" : "Добавить"}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
});

export default PartsModalBody;
